import { Action } from '~/ui/Actions';
import AddIcon from '~/assets/svg/newSvg/actions/add.svg?react';
import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';

import styles from './PromotionConditionBoard.module.scss';
import { ICondition } from '~/components/PromotionsBoard/StockBoard/StockBoard';
import ConditionGroupCard, {
  IConditionGroup,
} from './ConditionGroupCard/ConditionGroupCard';

interface IConditionsProps {
  conditionGroups: IConditionGroup[];
  onOperatorChange: (groupId: string, operator: 'and' | 'or') => void;
  onAddCondition: (groupId: string, condition: ICondition) => void;
  onDeleteCondition: (groupId: string, conditionId: string, item: { rule_id: number }) => void;
  onEditCondition: (groupId: string, condition: ICondition) => void;
  onDeleteGroup: (groupId: string) => void;
  onAddGroup: (interOperator: 'and' | 'or') => void;
  disabled?: boolean;
  isEdit?: boolean;
  hasError?: boolean;
}

const PromotionConditionBoard = ({
  conditionGroups,
  onOperatorChange,
  onAddCondition,
  onDeleteCondition,
  onEditCondition,
  onDeleteGroup,
  onAddGroup,
  hasError = false,
  disabled = false,
  isEdit = false,
}: IConditionsProps) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleAddGroup = (interOperator: 'and' | 'or') => {
    onAddGroup(interOperator);
    setIsDropdownOpen(false);
  };

  const handleDropdownToggle = () => {
    setIsDropdownOpen(prev => !prev);
  };

  const handleDropdownBlur = (e: React.FocusEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.relatedTarget)) {
      setIsDropdownOpen(false);
    }
  };

  return (
    <div className={styles.board}>
      {conditionGroups.map((group, index) => (
        <div key={group.id}>
          {index > 0 && (
            <div className={styles.groupSeparator}>
              <span className={styles.separatorLine} />
              <span className={styles.separatorLabel}>
                {group.inter_operator === 'or' ? t('condition_or') : t('condition_and')}
              </span>
              <span className={styles.separatorLine} />
            </div>
          )}
          <ConditionGroupCard
            group={group}
            groupIndex={index}
            disabled={disabled}
            isEdit={isEdit}
            canDelete={conditionGroups.length > 1}
            hasError={hasError && group.bindings.length === 0}
            onOperatorChange={onOperatorChange}
            onAddCondition={onAddCondition}
            onDeleteCondition={onDeleteCondition}
            onEditCondition={onEditCondition}
            onDeleteGroup={onDeleteGroup}
          />
        </div>
      ))}

      <div
        className={styles.addGroupWrapper}
        ref={dropdownRef}
        onBlur={handleDropdownBlur}
      >
        <Action
          variant="success"
          onClick={handleDropdownToggle}
          className={styles.addGroupBtn}
          disabled={disabled}
        >
          <AddIcon /> {t('add_condition_group')}
        </Action>
        {isDropdownOpen && (
          <div className={styles.dropdown}>
            <button
              type="button"
              className={styles.dropdownItem}
              onClick={() => handleAddGroup('and')}
            >
              {t('condition_and')}
            </button>
            <button
              type="button"
              className={styles.dropdownItem}
              onClick={() => handleAddGroup('or')}
            >
              {t('condition_or')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromotionConditionBoard;
